/**
 * Labstep
 *
 * @module components/ProtocolValue/SmartTableCollapsibleCard
 * @desc Component to show a collapsible smart table protocolValue card
 */

import { EntityViewFormShowEditName } from 'labstep-web/components/EntityView/Form/ShowEdit/Name';
import { ProtocolValueSmartTableModal } from 'labstep-web/components/ProtocolValue/SmartTable/Modal';
import { DataGridContextProvider } from 'labstep-web/contexts/grid';
import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import type { ParamsStateContextType } from 'labstep-web/contexts/params-state/types';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import { CollapsibleCard } from 'labstep-web/core/Collapsible/Card';
import { Flex } from 'labstep-web/core/Flex';
import { Icon } from 'labstep-web/core/Icon';
import { ExportProgressProvider } from 'labstep-web/grid/Index/components/DataGridExportAsCSV/context';
import { ToolPanelProvider } from 'labstep-web/grid/Index/components/ToolPanel/context';
import {
  getSystemFilterExperimentGuid,
  getSystemFilterProtocolGuid,
} from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition.service';
import { GridSmartTable as GridSmartTableProtocolValue } from 'labstep-web/grid/SmartTableProtocolValue';
import { GridFilterService } from 'labstep-web/grid/services/grid-filter.service';
import { EntityView } from 'labstep-web/models/entity-view.model';
import { Experiment } from 'labstep-web/models/experiment.model';
import type React from 'react';
import type { ProtocolValueSmartTableCollapsibleCardProps } from './types';

export const ProtocolValueSmartTableCollapsibleCard: React.FC<
  ProtocolValueSmartTableCollapsibleCardProps
> = ({
  entityView,
  experimentOrProtocol,
  initialExpandedState = true,
  group,
  expandComponent,
  disableHeader = false,
}) => {
  const globalParams: Record<
    string,
    string | number | Record<string, unknown>[]
  > = {
    count: 100,
    serializerGroups: 'smart_table,paginator',
  };

  const systemFilter =
    experimentOrProtocol instanceof Experiment
      ? getSystemFilterExperimentGuid(experimentOrProtocol.guid)
      : getSystemFilterProtocolGuid(experimentOrProtocol.guid);

  const paramsStateContext: ParamsStateContextType = {
    isGrid: true,
    name: 'smart_table',
    injectFields: {
      [experimentOrProtocol.entityName]: {
        id: experimentOrProtocol.id,
      },
    },
    globalParams,
    entityView,
    initialSearchParams:
      GridFilterService.injectSystemFilter(
        systemFilter,
        entityView.parameters,
      ) || {},
    loadedEntityView: new EntityView({
      ...entityView,
      parameters:
        GridFilterService.injectSystemFilter(
          systemFilter,
          entityView.parameters,
        ) || {},
    }),
  };

  return (
    <ParamsStateContextProvider value={paramsStateContext}>
      <ExportProgressProvider>
        <ToolPanelProvider>
          <CollapsibleCard>
            <CollapsibleCard.Item
              key="protocol-condition-smart-table-card-item"
              name={
                disableHeader ? (
                  <Flex>
                    <Icon name={'random'} />
                    <span>{entityView.name}</span>
                  </Flex>
                ) : (
                  <EntityViewFormShowEditName
                    entityView={entityView}
                  />
                )
              }
              actions={
                expandComponent ? (
                  expandComponent
                ) : (
                  <DataGridContextProvider>
                    <ProtocolValueSmartTableModal
                      experimentOrProtocol={experimentOrProtocol}
                      entityView={entityView}
                      viewComponent={({ toggleModal }) => (
                        <ActionComponent
                          dataCy="components/ProtocolValue/SmartTableCollapsibleCard:fullscreen"
                          type="icon"
                          icon="external alternate"
                          elementProps={{ color: 'blue' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleModal();
                          }}
                        />
                      )}
                    />
                  </DataGridContextProvider>
                )
              }
              disabled={disableHeader}
              content={
                <Flex
                  grow
                  height={551}
                  data-cy="components/ProtocolValue/SmartTableCollapsibleCard"
                >
                  <DataGridContextProvider>
                    <GridSmartTableProtocolValue
                      experimentOrProtocol={experimentOrProtocol}
                      entityName={entityView.entity_name}
                      group={group}
                      entities={experimentOrProtocol.protocol_values}
                      entityView={entityView}
                      fullscreen={false}
                    />
                  </DataGridContextProvider>
                </Flex>
              }
              initialExpandedState={initialExpandedState}
            />
          </CollapsibleCard>
        </ToolPanelProvider>
      </ExportProgressProvider>
    </ParamsStateContextProvider>
  );
};
